/**
 * Invoice numbering.
 *
 * § 14 Abs. 4 Nr. 4 UStG requires a sequential number, assigned once, that
 * uniquely identifies the invoice. The tax administration reads a gap as a
 * suppressed invoice, so the rules here are strict: numbers are drawn in
 * order, never reused, and never reassigned once issued.
 *
 * The practical consequence for the UI is that a draft invoice must not
 * consume a number. A number is allocated at the moment of issue and not
 * before, otherwise abandoning a draft would punch a hole in the sequence.
 */

export interface NumberCircleConfig {
  /** Literal prefix, e.g. `RE`. */
  prefix: string;
  /** Include the year in the number and restart the counter each year. */
  yearly: boolean;
  /** Zero-padded width of the counter, e.g. 4 gives `0001`. */
  padding: number;
  /** Separator between the parts. */
  separator: string;
}

export const DEFAULT_NUMBER_CIRCLE: NumberCircleConfig = {
  prefix: 'RE',
  yearly: true,
  padding: 4,
  separator: '-',
};

/** The circle an invoice issued on `isoDate` belongs to, e.g. `RE-2026`. */
export function numberCircleFor(config: NumberCircleConfig, isoDate: string): string {
  return config.yearly
    ? `${config.prefix}${config.separator}${isoDate.slice(0, 4)}`
    : config.prefix;
}

export function formatInvoiceNumber(
  config: NumberCircleConfig,
  isoDate: string,
  counter: number,
): string {
  const padded = String(counter).padStart(config.padding, '0');
  return `${numberCircleFor(config, isoDate)}${config.separator}${padded}`;
}

/**
 * Pull the counter out of a formatted number.
 *
 * Returns null for anything that does not match the circle's shape, so a
 * hand-edited number cannot silently reset the sequence.
 */
export function parseCounter(
  config: NumberCircleConfig,
  circle: string,
  invoiceNumber: string,
): number | null {
  const prefix = `${circle}${config.separator}`;
  if (!invoiceNumber.startsWith(prefix)) return null;
  const rest = invoiceNumber.slice(prefix.length);
  if (!/^\d+$/.test(rest)) return null;
  return Number.parseInt(rest, 10);
}

/**
 * Next number for a circle, given every number already issued in it.
 *
 * Deliberately takes the full list rather than a stored counter: the counter
 * is derived from the data, so a restored backup cannot desynchronise it.
 */
export function nextInvoiceNumber(
  config: NumberCircleConfig,
  isoDate: string,
  existingNumbers: readonly string[],
): { invoiceNumber: string; circle: string; counter: number } {
  const circle = numberCircleFor(config, isoDate);
  let highest = 0;
  for (const number of existingNumbers) {
    const counter = parseCounter(config, circle, number);
    if (counter !== null && counter > highest) highest = counter;
  }
  const counter = highest + 1;
  return { invoiceNumber: formatInvoiceNumber(config, isoDate, counter), circle, counter };
}

export interface SequenceGap {
  circle: string;
  missing: number[];
}

/**
 * Find holes in the issued sequence.
 *
 * Surfaced in the settings screen and written into the GoBD export, because
 * an auditor will run exactly this check and it is better to find a gap
 * yourself than to have it found for you.
 */
export function findSequenceGaps(
  config: NumberCircleConfig,
  numbersByCircle: ReadonlyMap<string, readonly string[]>,
): SequenceGap[] {
  const gaps: SequenceGap[] = [];

  for (const [circle, numbers] of numbersByCircle) {
    const counters = numbers
      .map((n) => parseCounter(config, circle, n))
      .filter((n): n is number => n !== null)
      .sort((a, b) => a - b);

    if (counters.length === 0) continue;

    const missing: number[] = [];
    // The sequence must start at 1; a first invoice numbered 5 is itself a gap.
    for (let expected = 1; expected <= counters[counters.length - 1]!; expected += 1) {
      if (!counters.includes(expected)) missing.push(expected);
    }
    if (missing.length > 0) gaps.push({ circle, missing });
  }

  return gaps;
}

/** True when the number is well-formed for its circle. */
export function isValidInvoiceNumber(
  config: NumberCircleConfig,
  circle: string,
  invoiceNumber: string,
): boolean {
  return parseCounter(config, circle, invoiceNumber) !== null;
}
